<template>
  <div class="'overflow-hidden'" style="border-radius: 12px">
    <div v-if="isEditingLayout && canEditLayout" class="p-50 m-50 zindex-4 settings">
      <!-- Delete Element -->
      <feather-icon
        icon="Trash2Icon"
        size="20"
        role="button"
        class="text-primary pointer"
        @click.stop.prevent="isUnshareModalVisible = true"
      />
      <!-- Edit Element -->
      <feather-icon
        icon="Edit2Icon"
        size="20"
        class="text-primary ml-1 pointer"
        role="button"
        @click="openModal"
      />
    </div>

    <!-- Type Text -->
    <div 
      v-if="element.type === 'text'" 
      class="m-2 border-radius text-element" 
      v-html="translate(element.customizations.text)" 
    />

    <!-- Type Image -->
    <div
      v-if="element.type === 'image'"
      class="p-0 border-radius d-flex align-items-center justify-content-center w-100"
      style="width: 100%;"
    >
      <img
        v-if="!isEditingLayout"
        :src="imageSrc"
        :alt="element.customizations.alt || 'Imagen'"
        class="background-image-container"
        :style="imageDisplayStyle"
        @click="handleImageClick"
      />
      <img
        v-else
        :src="imageSrc"
        :alt="element.customizations.alt || 'Imagen'"
        class="background-image-container"
        :style="{ ...imageDisplayStyle, cursor: 'move' }"
      />
    </div>

    <!-- Type Iframe -->
    <div
      v-if="element.type === 'iframe'"
      class="p-0 w-100 h-100 border-radius"
    >
      <iframe
        :src="element.customizations.linkURL"
        class="h-100 w-100 border-0"
        :style="isEditingLayout?
          'pointer-events:none':''"
      />
    </div>

    <!-- Type Video -->
    <div
      v-if="element.type === 'video'"
      class="border-radius"
    >
      <b-embed
        v-if="element.customizations.isLink"
        :type="element.customizations.isLink?'iframe':'video'"
        controls
        :src="srcVideo"
        aspect="16by9"
        allowfullscreen
        :style="element.customizations.isLink && isEditingLayout?
          'pointer-events:none':''"
      />
      <b-embed
        v-else
        controls
        aspect="16by9"
        allowfullscreen
        type="video"
      >
        <source :src="element.content">
      </b-embed>
    </div>

    <!-- Type Separator -->
    <div v-if="element.type === 'separator'">
      <!-- Type Horizontal -->
      <div v-if="element.customizations.separator==='horizontal'" class="mt-1 horizontal-separator">
        <p v-if="element.customizations.separatorTitle" class="h4 ml-2 text-primary">
          {{ translate(element.customizations.separatorTitle) }}
        </p>
        <div class="line line__horizontal" />
      </div>
      <!-- Type Vertical -->
      <div v-if="element.customizations.separator==='vertical' && !isGridDisabled" class="line line__vertical" />
    </div>

    <!-- Type Call to Action call-to-action (CTA) image in BG -->
    <div
      v-if="element.type === 'cta'"
      class="p-2 d-flex flex-column h-100 w-100 justify-content-center align-items-center border-radius"
      :style="element.content ? `background-image:url(${element.content}); background-size: ${getImageSize(element.customizations.imageSize)}; background-position: center; background-repeat: no-repeat;`:''"
    >
      <!--       <div
        v-if="!element.content"
        :class="isGridDisabled?'':'position-absolute'"
        class="w-100 h-100"
        style="background-color:rgba(255,255,255,0.6)"
      /> -->
      <h4 v-if="element.customizations.ctaTitle" class="zindex-1">
        {{ translate(element.customizations.ctaTitle) }}
      </h4>
      <p v-if="element.customizations.ctaHeadline" class="zindex-1">
        {{ translate(element.customizations.ctaHeadline) }}
      </p>
      <b-button
        v-if="element.customizations.ctaButton && element.customizations.ctaButtonLink"
        :target="getTarget(element.customizations.ctaButtonLink)"
        variant="primary"
        class="zindex-1"
        :href="element.customizations.ctaButtonLink"
      >
        {{ translate(element.customizations.ctaButton) }}
      </b-button>
    </div>

    <!-- Type Call to Action (CTA 2) image under title -->
    <div
      v-if="element.type === 'cta2'"
      class="p-3 d-flex flex-column h-100 w-100 justify-content-center align-items-center border-radius"
    >
      <h4 v-if="element.customizations.ctaTitle" class="zindex-1">
        {{ translate(element.customizations.ctaTitle) }}
      </h4>
      <!-- <img 
        :src="element.content" 
        class="cta-img"
      /> -->
      <div 
        :style="element.content ? `background-image:url(${element.content}); background-size: ${getImageSize(element.customizations.imageSize)}; background-position: center; background-repeat: no-repeat; width:100%; height: 100%; min-height: 100px;`:''"
        class="cta-img"
      >
        &nbsp;
      </div>
      <p v-if="element.customizations.ctaHeadline" class="zindex-1">
        {{ translate(element.customizations.ctaHeadline) }}
      </p>
      <b-button
        v-if="element.customizations.ctaButton && element.customizations.ctaButtonLink"
        :target="getTarget(element.customizations.ctaButtonLink)"
        variant="primary"
        class="zindex-1"
        :href="element.customizations.ctaButtonLink"
      >
        {{ translate(element.customizations.ctaButton) }}
      </b-button>
    </div>

    <!-- Type CTA 3 -->
    <div
      v-if="element.type === 'cta3'"
      class="cta3-card"
    >
      <!-- Header -->
      <div
        class="cta3-header"
        :style="element.customizations.ctaTitleBgColor ? `background-color: ${element.customizations.ctaTitleBgColor};` : ''"
        v-if="element.customizations.ctaTitle && translate(element.customizations.ctaTitle)"
      >
        <h4 class="cta3-title">
          {{ translate(element.customizations.ctaTitle) }}
        </h4>
      </div>
      <!-- Description -->
      <div class="cta3-body">
        <p
          v-if="element.customizations.text"
          class="cta3-description"
          v-html="translate(element.customizations.text)"
        ></p>
        <!-- Button -->
        <b-button
          v-if="cta3ButtonText && cta3ButtonLink"
          :target="getTarget(cta3ButtonLink)"
          variant="primary"
          class="cta3-btn"
          :href="cta3ButtonLink"
        >
          {{ cta3ButtonText }}
        </b-button>
      </div>
    </div>

    <!-- Delete Element Modal -->
    <unshare-modal
      v-model="isUnshareModalVisible"
      model-type="element"
      @remove-item="handleRemoveItem"
    />

    <!-- Create Element Modal -->
    <modal-create-element
      v-model="isManageElementModalVisible" 
      :item-to="element" 
      @edited="setEditedItem" 
    />
  </div>
</template>

<script>
import { getImageResource } from '@/@core/utils/image-utils';
import { translateTranslationTable } from '@/@core/libs/i18n/utils';
import EventsPlaceholder from '@/assets/images/placeholders/light/events.svg';
import GridStatusMixin from '@core/mixins/apps-layout/GridStatusMixin';
import UnshareModal from '@/@core/components/modal/UnshareModal.vue';
import ToastNotificationsMixin from '@core/mixins/toast-notifications/ToastNotificationsMixin';
import { ENABLED_APPS_GETTERS } from '@/store/enabled-apps/enabled-apps-store-constants';
import ModalCreateElement from '@/@core/components/layouts/apps-layout/elements-layout/ModalCreateElement.vue';
import {
  isSuccessfulElementResponse,
  isSuccessfulHttpResponse,
} from '@/@core/utils/element-api';

export default {
  name: 'ElementsListWidget',
  components: { UnshareModal, ModalCreateElement },
  mixins: [GridStatusMixin, ToastNotificationsMixin],

  props: {
    element: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isUnshareModalVisible: false,
      isManageElementModalVisible: false,
    };
  },
  async created() {
    // console.log('this.element', this.element);
  },
  computed: {
    placeholder() {
      return EventsPlaceholder;
    },
    isHome() {
      return this.$route.name === 'Resumen';
    },
    canEditLayout() {
      return this.isHome && !this.isGridDisabled;
    },
    isEditingLayout() {
      return this.$store.getters[ENABLED_APPS_GETTERS.isEditingLayout];
    },
    srcVideo() {
      return this.element.customizations.linkURL.replace('watch?v=', 'embed/');
    },
    imageSrc() {
      const c = this.element.customizations || {};
      return c.linkURL || this.element.content || this.placeholder;
    },
    imageDisplayStyle() {
      const size = this.element.customizations?.imageSize;
      const objectFit = (size === 'cover' || size === 'contain') ? size : 'cover';
      return {
        width: '100%',
        height: '100%',
        display: 'block',
        objectFit,
      };
    },
    cta3ButtonText() {
      const customizations = this.element.customizations || {};
      const buttonField = customizations.ctaButton || customizations.button;
      return this.translate(buttonField);
    },
    cta3ButtonLink() {
      const customizations = this.element.customizations || {};
      return customizations.ctaButtonLink || customizations.buttonLink || '';
    },
  },
  methods: {
    setEditedItem(response) {
      this.element.content = response.content;
      this.element.customizations = response.customizations;
    },
    setAltImg(event) {
      setTimeout(() => {
        event.target.src = this.element.content;
      }, 1000);
    },
    translate(field) {
      return translateTranslationTable(this.$store.state.locale.currentLocale, field);
    },
    getImageSrc(url) {
      return getImageResource(url);
    },
    openModal() {
      this.isManageElementModalVisible = true;
      // this.$bvModal.show('modal-create-element');
    },

    async handleRemoveItem(response) {
      if (response !== true) return;
      try {
        const deleteResponse = await this.$store.dispatch('createItem', {
          item: {
            itemType: '/elements/delete',
            customName: 'elements',
            requestConfig: {
              key: this.element.key,
            },
          },
        });
        if (!isSuccessfulHttpResponse(deleteResponse)
          || !isSuccessfulElementResponse(deleteResponse.data)) {
          this.notifyError(this.$t('error-message.general-error'));
          return;
        }
        this.notifySuccess(this.$t('layouts.message.saved-success'));
      } catch {
        this.notifyError(this.$t('error-message.general-error'));
      }
    },
    getTarget(link) {
      const isNexos = process.env.VUE_APP_CUSTOMER === 'nexos';;
      // console.log('link', link);
      let isInternalLink = false;
      
      if (isNexos) {
        isInternalLink = link.includes('nexos.bancolombia.com') || link.includes('http://localhost:8080/');
      } else {
        isInternalLink = link.includes('.nectios.com') || link.includes('.nectios-staging.com') || link.includes('http://localhost:8080/');
      }
      
      // console.log('isInternalLink', isInternalLink);
      return isInternalLink ? '' : '_blank';
    },
    getImageSize(imageSize){
      return imageSize ?? 'cover';
    },
    handleImageClick() {
      if (this.element.customizations.imageURLButton) {
        const isNexos = process.env.VUE_APP_CUSTOMER === 'nexos';
        const target = isNexos ? '_self' : '_blank';
        window.open(this.element.customizations.imageURLButton, target);
      }
    }
  },

};
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/bootstrap-include";
.line{
  background-color:$primary;
  &__vertical{
    width:3px;
    min-height:120px;
    height:100%;
    margin:auto
  }
  &__horizontal{
    height:3px;
  }
}
.imatge {
   filter: drop-shadow(0px 5px 10px rgba(0, 0, 0, 0.1));
   white-space: normal;
   margin-top: .5rem;
   margin-bottom: 1.5rem;
   background-color: #fff;
   border-radius: 12px;
   padding: 18px;
   overflow: hidden;
   vertical-align: top;
}
.settings{
  position:fixed;
  right:15px;
  background-color:lighten($primary, 50%);
  border-radius: 12px;
  z-index: 10;
}
.text-element::v-deep img {
  max-width: 100% !important;
  height: auto !important;
}
.border-radius{
    border-radius: 12px;
    overflow: hidden;
}

.horizontal-separator {
  position: relative;
  width: 100%;
  padding: 0.5rem 0;
}

.cta-img {
  max-height: calc(100% - 80px); 
  border-radius: 1rem; 
  margin-bottom: 1rem;
}

.background-image-container {
  width: 100%;
  height: 100%;
  transition: all 0.3s ease;
  
  &.clickable {
    cursor: pointer;
    
    &:hover {
      opacity: 0.9;
    }
  }
}

// Mobile-specific styles
@media (max-width: 767px) {
  .horizontal-separator {
    position: unset;
    margin: 1rem 0;
  }

  .settings {
    position: fixed;
    right: 10px;
    padding: 8px;
    margin: 8px;
  }

  .text-element {
    padding: 0rem;
    font-size: 0.95rem;
  }

  .cta-img {
    max-height: 200px;
    margin: 0.5rem 0;
  }

  // Adjust padding for all element types on mobile
  [class*="p-"] {
    padding: 0.75rem !important;
  }

  // Adjust margins for better spacing
  [class*="m-"] {
    margin: 0.5rem !important;
  }

  // Make buttons more touch-friendly
  .btn {
    padding: 0.5rem 1rem;
    font-size: 0.95rem;
    width: 100%;
    margin: 0.5rem 0;
  }

  // Adjust headings for better readability
  h4 {
    font-size: 1.25rem;
    margin-bottom: 0.75rem;
  }

  p {
    font-size: 0.95rem;
    margin-bottom: 0.75rem;
  }
}

.cta3-card {
  border-radius: 24px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  overflow: hidden;
  background: #fff;
  margin: 1rem 0;
}

.cta3-header {
  padding: 1.5rem 1rem 1rem 1rem;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  text-align: center;
}

.cta3-title {
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: #222;
  text-align: center;
}

.cta3-body {
  padding: 1.5rem 1rem 1rem 1rem;
}

.cta3-description {
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 1.5rem;
}

.cta3-btn {
  margin-top: 0.5rem;
  min-width: 180px;
}
</style>
